import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { addKlientNote, deleteKlientNote, getKlientNotes, updateKlientNote, type KlientNote } from '../api/klienti';
import { fmtDay } from './klienti-format';
import { confirmDialog } from '../shell/dialogs';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '../components/arc/button/button';
import { PageState } from '../components/arc/page-state/page-state';
import { ArcPanel } from '../components/arc/primitives/arc-primitives';

export function KlientPoznamky({ id, canManage, onChanged }: { id: number; canManage: boolean; onChanged?: () => void }) {
  const [rows, setRows] = useState<KlientNote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [novy, setNovy] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getKlientNotes(id)
      .then((d) => { if (!cancelled) { setRows(d); setError(null); } })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Načtení poznámek selhalo'); });
    return () => { cancelled = true; };
  }, [id, version]);

  const uloz = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      setVersion((v) => v + 1);
      setError(null);
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Uložení selhalo');
    } finally {
      setBusy(false);
    }
  };

  if (error && !rows) return <PageState title="Poznámky se nepodařilo načíst" message={error} />;
  if (!rows) return <PageState title="Načítám poznámky" loading />;

  return (
    <div className="klient-poznamky">
      {error && <p className="klienti-create-error" role="alert">{error}</p>}
      {canManage && (
        <div className="poznamka-novy">
          <textarea
            className="klient-note-input"
            rows={3}
            placeholder="Nová poznámka - podmínky spolupráce, dohody, kontext…"
            value={novy}
            onChange={(e) => setNovy(e.target.value)}
          />
          <Button
            type="button"
            size="sm"
            disabled={busy || novy.trim().length === 0}
            onClick={() => uloz(async () => { await addKlientNote(id, novy.trim()); setNovy(''); })}
          >
            Přidat poznámku
          </Button>
        </div>
      )}
      {rows.length === 0 && <div className="text-muted py-3">U tohoto klienta nejsou žádné poznámky.</div>}
      <ol className="poznamky-seznam">
        {rows.map((n) => (
          <ArcPanel as="li" className="poznamka klient-note-card" key={n.id}>
            <header className="poznamka-head">
              <span className="poznamka-datum">{fmtDay(n.notedAt)}</span>
              {n.source !== 'p1' && <span className="poznamka-zdroj">{n.source}</span>}
              {canManage && editId !== n.id && (
                <span className="poznamka-akce">
                  <Button type="button" variant="ghost" size="sm" className="klient-note-icon-btn" aria-label="Upravit poznámku" title="Upravit" onClick={() => { setEditId(n.id); setEditText(n.body); }}>
                    <Pencil size={15} aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="klient-note-icon-btn"
                    aria-label="Smazat poznámku"
                    title="Smazat"
                    disabled={busy}
                    onClick={() => {
                      void confirmDialog({ title: 'Smazat poznámku?', confirmLabel: 'Smazat', danger: true }).then((ok) => { if (ok) void uloz(() => deleteKlientNote(id, n.id)); });
                    }}
                  >
                    <Trash2 size={15} aria-hidden="true" />
                  </Button>
                </span>
              )}
            </header>
            {editId === n.id ? (
              <div className="poznamka-editace">
                <textarea className="klient-note-input" rows={Math.min(14, Math.max(3, editText.split('\n').length + 1))} value={editText} onChange={(e) => setEditText(e.target.value)} />
                <div className="poznamka-editace-akce">
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy || editText.trim().length === 0}
                    onClick={() => uloz(async () => { await updateKlientNote(id, n.id, editText.trim()); setEditId(null); })}
                  >
                    Uložit
                  </Button>
                  <Button type="button" variant="secondary" size="sm" onClick={() => setEditId(null)}>Zrušit</Button>
                </div>
              </div>
            ) : (
              <p className="poznamka-telo">{n.body}</p>
            )}
          </ArcPanel>
        ))}
      </ol>
    </div>
  );
}
